// apps/frontend_fl/src/components/modals/listaFornecedores/index.tsx
import { INotificationMessage } from "@fl/shared/src/types/messages";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useState } from "react";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { InferType } from "yup";

import { cadastroListaFornecedoresSchema } from "../../../schemas/listaFornecedores";
import AlterMessages from "../../alerts/indes";

// --- Tipos próprios do modal (desacoplados do shared) ---
type FornecedorItemInput = { id_fornecedor: number | string };

export type CadastroListaFornecedoresFormInputs = InferType<
  typeof cadastroListaFornecedoresSchema
>;

export type ListaFornecedoresResult = {
  id_lista?: number;
  nome_lista: string;
  observacao: string | null;
  itens?: FornecedorItemInput[];
};

type ListaFornecedoresDataIn = Partial<{
  id_lista: number;
  nome_lista: string;
  observacao: string | null;
  // pode vir de qualquer forma: number[] | {id_fornecedor}[] | undefined
  itens: Array<number | string | FornecedorItemInput> | null;
}>;

interface IProps {
  showModal: boolean;
  data: ListaFornecedoresDataIn | null;
  onResult: (item: ListaFornecedoresResult | null) => void;
}

const ListaFornecedoresModal: React.FC<IProps> = ({
  showModal,
  data,
  onResult,
}) => {
  const [messages, setMessages] = useState<INotificationMessage[]>([]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CadastroListaFornecedoresFormInputs>({
    resolver: yupResolver(cadastroListaFornecedoresSchema),
    defaultValues: { nome_lista: "", observacao: "" },
  });

  useEffect(() => {
    if (showModal) {
      reset({
        nome_lista: data?.nome_lista ?? "",
        observacao: data?.observacao ?? "",
      });
    } else {
      reset({ nome_lista: "", observacao: "" });
    }
  }, [showModal, data, reset]);

  const onSubmit = handleSubmit((formData) => {
    // Normaliza itens para SEMPRE virar { id_fornecedor }[]
    const itensNorm: FornecedorItemInput[] = Array.isArray(data?.itens)
      ? data!
          .itens!.map((x) =>
            typeof x === "number" || typeof x === "string"
              ? { id_fornecedor: x }
              : { id_fornecedor: (x as FornecedorItemInput).id_fornecedor }
          )
          .filter(
            (i) => i.id_fornecedor !== undefined && i.id_fornecedor !== null
          )
      : [];

    onResult({
      id_lista: data?.id_lista,
      nome_lista: formData.nome_lista!.trim(),
      observacao: (formData.observacao ?? "").trim() || null,
      itens: itensNorm,
    });
  });

  return (
    <Modal show={showModal} onHide={() => onResult(null)} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Cadastro de Lista de Fornecedores</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form onSubmit={onSubmit}>
          <Row className="mt-2">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Nome da Lista</Form.Label>
                <Form.Control
                  type="text"
                  autoFocus
                  {...register("nome_lista")}
                  isInvalid={!!errors.nome_lista}
                  placeholder="Ex.: Prestadores recorrentes"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.nome_lista?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mt-2">
            <Col>
              <Form.Group>
                <Form.Label>Observação</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  {...register("observacao")}
                  isInvalid={!!errors.observacao}
                  placeholder="Use para identificar o objetivo da lista"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.observacao?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <AlterMessages
            messages={messages}
            setMessages={setMessages}
            floating={false}
          />

          <div className="w-100 d-flex justify-content-between mt-4">
            <Button
              variant="danger"
              type="button"
              onClick={() => onResult(null)}
            >
              Cancelar
            </Button>
            <Button variant="success" type="submit" disabled={isSubmitting}>
              Confirmar
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default ListaFornecedoresModal;
